import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';

interface Notice {
  notice_id: string; institute_id: string; branch_id?: string;
  batch_id?: string | null; broadcast_type: string; sent_to?: string[];
  broadcast_to?: string; notice_title: string; notice_body: string;
  files: any[]; created_at: string; created_by: string;
}

interface Batch {
  batch_id: string; institute_id: string; branch_id: string; course_id: string;
  batch_name: string; batch_time: string; start_month: string; start_year: number;
  end_month: string; end_year: number; is_active: boolean; created_at: string | null;
}

interface Branch { branch_id: string; branch_name: string; }

interface AddNoticeModalProps {
  visible: boolean; instituteId: string; branchId: string;
  editingNotice?: Notice | null; onHide: () => void; onSuccess?: () => void;
  availableBranches?: Branch[];
}

interface UploadedFile { file: File; name: string; size: number; preview?: string; }

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const getFileIcon = (name: string) => {
  const ext = name.slice(((name.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
  if (ext === 'pdf')                return 'pi pi-file-pdf';
  if (['doc','docx'].includes(ext)) return 'pi pi-file-word';
  if (['xls','xlsx'].includes(ext)) return 'pi pi-file-excel';
  if (['jpg','jpeg'].includes(ext)) return 'pi pi-image';
  return 'pi pi-file';
};

const getFileExt    = (name: string) => name.slice(((name.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
const formatFileSize = (b: number)   => { if (!b) return '0 B'; const k=1024,s=['B','KB','MB']; const i=Math.floor(Math.log(b)/Math.log(k)); return Math.round((b/Math.pow(k,i))*100)/100+' '+s[i]; };

const AddNoticeModal: React.FC<AddNoticeModalProps> = ({
  visible, instituteId, branchId, editingNotice, onHide, onSuccess, availableBranches,
}) => {
  const toast        = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [batches,         setBatches]         = useState<Batch[]>([]);
  const [branches,        setBranches]        = useState<Branch[]>(availableBranches || []);
  const [loadingBatches,  setLoadingBatches]  = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [uploadedFiles,   setUploadedFiles]   = useState<UploadedFile[]>([]);
  const [selectedBranches,setSelectedBranches]= useState<string[]>([]);
  const [errors,          setErrors]          = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    broadcast_type: 'branch',
    broadcast_to:   'all',
    batch_id:       '',
    notice_title:   '',
    notice_body:    '',
  });

  const MAX_FILES       = 3;
  const MAX_TOTAL_SIZE  = 6 * 1024 * 1024;
  const ALLOWED_FORMATS = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/jpg'
  ];
  const ALLOWED_EXTENSIONS = ['pdf','xls','xlsx','doc','docx','jpeg','jpg'];

  const showToast = (severity: 'success'|'error'|'warn'|'info', summary: string, detail: string, life = 3500) =>
    toast.current?.show({ severity, summary, detail, life });

  useEffect(() => {
    if (visible) {
      if (formData.broadcast_type === 'batch' && batches.length === 0) fetchBatches();
      if (formData.broadcast_type === 'multi_branch' && branches.length === 0 && !availableBranches) fetchBranches();
    }
  }, [visible, formData.broadcast_type]);

  useEffect(() => {
    if (editingNotice) {
      setFormData({
        broadcast_type: editingNotice.broadcast_type,
        broadcast_to:   editingNotice.broadcast_to || 'all',
        batch_id:       editingNotice.batch_id     || '',
        notice_title:   editingNotice.notice_title,
        notice_body:    editingNotice.notice_body,
      });
      setSelectedBranches(editingNotice.sent_to || []);
      setUploadedFiles([]);
    } else {
      setFormData({ broadcast_type: 'branch', broadcast_to: 'all', batch_id: '', notice_title: '', notice_body: '' });
      setSelectedBranches([]);
      setUploadedFiles([]);
    }
    setErrors({});
    setError(null);
  }, [editingNotice, visible]);

  const fetchBatches = async () => {
    try {
      setLoadingBatches(true);
      const res = await apiClient.get(`/v1/batches?page=1&limit=100`);
      if (res.data.success) setBatches(res.data.data || []);
      else showToast('error', 'Error', 'Failed to load batches');
    } catch (err: any) {
      showToast('error', 'Error', err.response?.data?.error || 'Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchBranches = async () => {
    try {
      setLoadingBranches(true);
      const res = await apiClient.get('/v1/branches', { params: { institute_id: instituteId, active: true } });
      if (res.data.success) setBranches(res.data.data || []);
      else showToast('error', 'Error', 'Failed to load branches');
    } catch (err: any) {
      showToast('error', 'Error', err.response?.data?.error || 'Failed to load branches');
    } finally {
      setLoadingBranches(false);
    }
  };

  const broadcastTypeOptions: DropdownOption[] = [
    { label: 'Single Branch',             value: 'branch' },
    { label: 'Multiple Branches',         value: 'multi_branch' },
    { label: 'Specific Batch (Students)', value: 'batch' },
    { label: 'Individual',                value: 'individual' },
  ];

  const broadcastToOptions: DropdownOption[] = [
    { label: 'All (Teachers + Students + Staff)', value: 'all' },
    { label: 'Teachers Only', value: 'teachers' },
    { label: 'Students Only', value: 'students' },
    { label: 'Staff Only',    value: 'staff' },
  ];

  const batchOptions:  DropdownOption[] = batches.map(b  => ({ label: `${b.batch_name} (${b.batch_time || 'N/A'})`, value: b.batch_id }));
  const branchOptions: DropdownOption[] = branches.map(b => ({ label: b.branch_name, value: b.branch_id }));

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'broadcast_type') { updated.batch_id = ''; setSelectedBranches([]); }
      return updated;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const getTotalSize = () => uploadedFiles.reduce((t, f) => t + f.size, 0);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    if (uploadedFiles.length + files.length > MAX_FILES) {
      showToast('error', 'Limit', `Max ${MAX_FILES} files allowed`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const newFiles: UploadedFile[] = [];
    const fileErrors: string[]     = [];
    Array.from(files).forEach(file => {
      const ext = getFileExt(file.name);
      if (!ALLOWED_FORMATS.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
        fileErrors.push(`${file.name}: Invalid format`);
        return;
      }
      newFiles.push({ file, name: file.name, size: file.size });
    });
    if (getTotalSize() + newFiles.reduce((s, f) => s + f.size, 0) > MAX_TOTAL_SIZE) {
      showToast('error', 'Size Limit', `Total cannot exceed ${formatFileSize(MAX_TOTAL_SIZE)}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (fileErrors.length) showToast('error', 'Invalid Files', fileErrors.join(', '));
    setUploadedFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index: number) => setUploadedFiles(prev => prev.filter((_, i) => i !== index));

  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!formData.broadcast_type) e.broadcast_type = 'Required';
    if (formData.broadcast_type === 'batch' && !formData.batch_id) e.batch_id = 'Batch is required';
    else if (formData.broadcast_type === 'multi_branch' && !selectedBranches.length) e.multi_branch = 'Select at least one branch';
    else if (formData.broadcast_type !== 'batch' && formData.broadcast_type !== 'multi_branch' && !formData.broadcast_to) e.broadcast_to = 'Audience is required';
    if (!formData.notice_title.trim()) e.notice_title = 'Title is required';
    if (!formData.notice_body.trim())  e.notice_body  = 'Content is required';
    setErrors(e);
    if (Object.keys(e).length) showToast('error', 'Validation Failed', 'Please correct all highlighted fields');
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);

    const payload = new FormData();

    // ── notice_id FIRST when editing ─────────────────────────────────────────
    if (editingNotice) {
      payload.append('notice_id', editingNotice.notice_id);
    }

    payload.append('institute_id',   instituteId);
    payload.append('broadcast_type', formData.broadcast_type);
    payload.append('notice_title',   formData.notice_title.trim());
    payload.append('notice_body',    formData.notice_body.trim());

    if (formData.broadcast_type === 'batch') {
      payload.append('batch_id',     formData.batch_id);
      payload.append('broadcast_to', 'students');
    } else if (formData.broadcast_type === 'multi_branch') {
      selectedBranches.forEach(id => payload.append('sent_to[]', id));
    } else if (formData.broadcast_type === 'branch') {
      payload.append('branch_id',    branchId);
      payload.append('broadcast_to', formData.broadcast_to);
    } else {
      payload.append('broadcast_to', formData.broadcast_to);
    }

    uploadedFiles.forEach(f => payload.append('files[]', f.file));

    // ── debug: log everything being sent ─────────────────────────────────────
   payload.forEach((value, key) => {
  console.log('FORMDATA ENTRY:', key, value);
});

    try {
      const response = editingNotice
        ? await apiClient.put(
            `/v1/noticeboard/by-branch/${branchId}`,
            payload
            // ❌ do NOT set Content-Type manually — let browser set it with boundary
          )
        : await apiClient.post(
            '/v1/noticeboard',
            payload
          );

      if (response.data.success) {
        showToast(
          'success',
          editingNotice ? 'Notice Updated' : 'Notice Published',
          editingNotice ? 'Notice updated successfully.' : 'Notice published successfully.'
        );
        onSuccess?.();
        setTimeout(() => onHide(), 900);
      } else {
        showToast('error', 'Error', response.data.message || 'Operation failed');
      }
    } catch (err: any) {
      console.log('FULL ERROR RESPONSE:', err.response?.data);
      console.log('STATUS CODE:', err.response?.status);
      showToast('error', 'Error',
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to save notice.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ broadcast_type: 'branch', broadcast_to: 'all', batch_id: '', notice_title: '', notice_body: '' });
    setSelectedBranches([]);
    setUploadedFiles([]);
    setErrors({});
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onHide();
  };

  const totalSize    = getTotalSize();
  const canUploadMore = uploadedFiles.length < MAX_FILES && totalSize < MAX_TOTAL_SIZE;

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: '24px' } }
      }}
    >
      <Toast ref={toast} position="top-right" />

      <div>
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '16px', padding: '28px', marginBottom: '24px',
          color: C.white, position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '180px', height: '180px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-40px', left: '20%', width: '120px', height: '120px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
              <i className="pi pi-bell" style={{ fontSize: '0.7rem' }} />
              Notice Management
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.5rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
              {editingNotice ? 'Edit Notice' : 'Create New Notice'}
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
              {editingNotice ? 'Update notice details below' : 'Share important information with your community'}
            </p>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />}

        {/* ── Form ────────────────────────────────────────────────────── */}
        <div style={{ background: C.white, borderRadius: '16px', padding: '24px', marginBottom: '20px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.06)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Broadcast Settings */}
            <SectionBox title="Broadcast Settings" icon="pi-megaphone">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <GlobalDropdownField
                  label="Broadcast Type" required value={formData.broadcast_type}
                  onChange={val => handleInputChange('broadcast_type', val)}
                  options={broadcastTypeOptions} placeholder="Select type"
                  error={errors.broadcast_type} disabled={!!editingNotice}
                />

                {formData.broadcast_type === 'batch' && (
                  <GlobalDropdownField
                    label="Select Batch" required value={formData.batch_id}
                    onChange={val => handleInputChange('batch_id', val)}
                    options={batchOptions} placeholder={loadingBatches ? 'Loading...' : 'Select batch'}
                    error={errors.batch_id} disabled={loadingBatches}
                  />
                )}

                {formData.broadcast_type === 'multi_branch' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: '600', color: C.navy }}>
                      Select Branches <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <select
                      multiple
                      value={selectedBranches}
                      onChange={e => setSelectedBranches(Array.from(e.target.selectedOptions).map(o => o.value))}
                      style={{ width: '100%', height: '140px', padding: '8px', border: `1px solid ${errors.multi_branch ? '#dc2626' : C.border}`, borderRadius: '8px', background: C.white, color: C.navy, fontSize: '0.85rem', outline: 'none' }}
                    >
                      {branchOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    {errors.multi_branch && <small style={{ color: '#dc2626', fontSize: '0.75rem' }}>{errors.multi_branch}</small>}
                  </div>
                )}

                {(formData.broadcast_type === 'branch' || formData.broadcast_type === 'individual') && (
                  <GlobalDropdownField
                    label="Send To" required value={formData.broadcast_to}
                    onChange={val => handleInputChange('broadcast_to', val)}
                    options={broadcastToOptions} placeholder="Select audience"
                    error={errors.broadcast_to}
                  />
                )}
              </div>

              {(formData.broadcast_type === 'batch' || formData.broadcast_type === 'multi_branch') && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '0.82rem', color: C.gold }} />
                  <span style={{ fontSize: '0.8rem', fontWeight: '600', color: C.white }}>
                    {formData.broadcast_type === 'batch'
                      ? 'Notice will be sent to all students in the selected batch.'
                      : 'Notice will be sent to all users in selected branches.'}
                  </span>
                </div>
              )}
            </SectionBox>

            {/* Title */}
            <GlobalInputField
              label="Notice Title" required value={formData.notice_title}
              onChange={e => handleInputChange('notice_title', e.target.value)}
              placeholder="e.g., Mid-Term Examination Schedule"
              error={errors.notice_title}
            />

            {/* Body */}
            <GlobalTextArea
              label="Notice Content" required value={formData.notice_body}
              onChange={e => handleInputChange('notice_body', e.target.value)}
              placeholder="Write the notice details here..."
              error={errors.notice_body} rows={6}
            />

            {/* Attachments */}
            <SectionBox
              title="Attachments"
              icon="pi-paperclip"
              badge={`${uploadedFiles.length}/${MAX_FILES} · ${formatFileSize(totalSize)} / ${formatFileSize(MAX_TOTAL_SIZE)}`}
            >
              <input
                ref={fileInputRef} type="file" multiple
                accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg"
                onChange={handleFileSelect} style={{ display: 'none' }}
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canUploadMore}
                style={{
                  width: '100%', padding: '18px',
                  border: `2px dashed ${canUploadMore ? C.goldBorder : C.border}`,
                  borderRadius: '10px',
                  background: canUploadMore ? 'rgba(251,178,31,0.04)' : C.bg,
                  color: canUploadMore ? C.navy : C.muted,
                  cursor: canUploadMore ? 'pointer' : 'not-allowed',
                  fontSize: '0.88rem', fontWeight: '600',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => { if (canUploadMore) { e.currentTarget.style.background = 'rgba(251,178,31,0.08)'; e.currentTarget.style.borderColor = C.gold; } }}
                onMouseLeave={e => { if (canUploadMore) { e.currentTarget.style.background = 'rgba(251,178,31,0.04)'; e.currentTarget.style.borderColor = C.goldBorder; } }}
              >
                <i className="pi pi-upload" style={{ fontSize: '0.9rem', color: canUploadMore ? C.gold : C.muted }} />
                {canUploadMore ? 'Choose files (max 6MB total)' : 'Upload limit reached'}
              </button>

              <p style={{ margin: '8px 0 0 0', fontSize: '0.72rem', color: C.muted, textAlign: 'center' }}>
                Allowed: PDF, Word, Excel, JPEG
              </p>

              {uploadedFiles.length > 0 && (
                <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {uploadedFiles.map((fileObj, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <div style={{ width: '36px', height: '36px', background: C.bg, borderRadius: '8px', border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className={getFileIcon(fileObj.name)} style={{ fontSize: '1.1rem', color: C.navy }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: '0 0 3px 0', fontSize: '0.85rem', fontWeight: '700', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fileObj.name}</p>
                          <p style={{ margin: 0, fontSize: '0.72rem', color: C.muted, fontWeight: '600' }}>{formatFileSize(fileObj.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        style={{ padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '7px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700', transition: 'all 0.2s ease', flexShrink: 0 }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </SectionBox>
          </div>
        </div>

        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlobalButton label="Cancel" variant="outline" onClick={handleClose} disabled={loading} />
          <GlobalButton
            label={loading
              ? (editingNotice ? 'Updating...' : 'Publishing...')
              : (editingNotice ? 'Update Notice'  : 'Publish Notice')}
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

// ── SectionBox ────────────────────────────────────────────────────────────────
const SectionBox: React.FC<{ title: string; icon: string; badge?: string; children: React.ReactNode }> = ({ title, icon, badge, children }) => (
  <div style={{ background: '#F5F7FA', borderRadius: '14px', padding: '18px', border: '1px solid #E5E7EB' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`pi ${icon}`} style={{ color: '#fbb21f', fontSize: '0.85rem' }} />
        </div>
        <span style={{ fontSize: '0.88rem', fontWeight: '700', color: '#0e225d' }}>{title}</span>
      </div>
      {badge && <span style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: '600' }}>{badge}</span>}
    </div>
    {children}
  </div>
);

export default AddNoticeModal;